import { ExternalLinkButton } from "@/components/blocks/ExternalLinkButton";
import { ProductShot } from "@/components/blocks/ProductShot";
import {
	VaerdictFollowUpScene,
	VaerdictSimulationScene,
} from "@/components/blocks/scenes/VaerdictScreens";
import { CardGrid, toCardItems } from "@/components/blocks/sections/CardGrid";
import { Cta } from "@/components/blocks/sections/Cta";
import { Facts } from "@/components/blocks/sections/Facts";
import { PageHero } from "@/components/blocks/sections/PageHero";
import { Section } from "@/components/blocks/sections/Section";
import {
	displayH2,
	Overline,
} from "@/components/blocks/sections/SectionHeading";
import { CaseBlock } from "@/components/blocks/work/CaseBlock";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/motion";
import { VAERDICT_URL } from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";

const CTA_ID = "contact";

export function VaerdictCasePage() {
	const { dict } = useI18n();
	const page = dict.vaerdictCase;

	return (
		<>
			<PageHero
				label={page.hero.label}
				title={page.hero.title}
				emphasis={page.hero.emphasis}
				intro={page.hero.intro}
				visual={
					<ProductShot
						alt={page.shots.simulation}
						caption={page.shots.simulation}
						fallback={<VaerdictSimulationScene />}
					/>
				}
				footer={
					<div className="mx-auto w-full max-w-7xl px-6 pb-16 lg:px-10">
						<Facts items={page.facts} />
					</div>
				}
			>
				<ExternalLinkButton
					href={VAERDICT_URL}
					cta="vaerdict"
					placement="case_hero"
					variant="outline"
				>
					{page.productLink}
				</ExternalLinkButton>
			</PageHero>

			<Section containerClassName="space-y-14">
				<CaseBlock index="01" title={page.situation.title}>
					<p>{page.situation.body}</p>
				</CaseBlock>
				<CaseBlock index="02" title={page.intervention.title}>
					<p>{page.intervention.body}</p>
				</CaseBlock>
				<CaseBlock index="03" title={page.solution.title}>
					<p>{page.solution.body}</p>
				</CaseBlock>
				<CardGrid
					items={toCardItems(page.solution.items)}
					columns={4}
					numbered
				/>
				<CaseBlock index="04" title={page.engine.title}>
					<p>{page.engine.body}</p>
					<RevealGroup as="ol" className="divide-y border-y text-base">
						{Object.entries(page.engine.steps).map(([key, step]) => (
							<RevealItem
								as="li"
								key={key}
								className="grid gap-1 py-4 sm:grid-cols-[6.5rem_12rem_1fr] sm:items-baseline sm:gap-6"
							>
								<span className="font-mono text-sm text-signal-ink">
									{step.symbol}
								</span>
								<span className="font-semibold text-foreground">
									{step.title}
								</span>
								<span>{step.detail}</span>
							</RevealItem>
						))}
					</RevealGroup>
					<p className="rounded-2xl border-l-2 border-signal bg-muted/60 p-5 text-base">
						{page.engine.note}
					</p>
				</CaseBlock>
			</Section>

			<Section tone="stone">
				<div className="grid grid-cols-[minmax(0,1fr)] items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:gap-16">
					<Reveal className="space-y-6">
						<Overline index="05">{page.followUp.label}</Overline>
						<h2 className={displayH2}>{page.followUp.title}</h2>
						<p className="text-lg text-pretty text-muted-foreground">
							{page.followUp.body}
						</p>
					</Reveal>
					<Reveal delay={0.1}>
						<ProductShot
							alt={page.shots.followUp}
							caption={page.shots.followUp}
							fallback={<VaerdictFollowUpScene />}
						/>
					</Reveal>
				</div>
			</Section>

			<Section containerClassName="space-y-14">
				<CaseBlock index="06" title={page.rollout.title}>
					<p>{page.rollout.body}</p>
				</CaseBlock>
				<CaseBlock index="07" title={page.today.title}>
					<p>{page.today.body}</p>
					<p className="rounded-2xl border-l-2 border-signal bg-muted/60 p-5 text-base">
						{page.today.note}
					</p>
				</CaseBlock>
			</Section>

			<Cta id={CTA_ID} {...page.cta} placement="case_cta" />
		</>
	);
}
